import { useContext, useEffect, useRef, useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { useParams } from "react-router-dom";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import dossierService from "api/dossierService";

import AddTagModal from "components/AddTagModal";
import ConfirmModal from "components/ConfirmModal";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import DossierFilesContext from "contexts/DossierFilesContext";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import { IPackage } from "dto/IPackage";
import { ITag } from "dto/ITag";
import LinkIcon from "assets/icons/link.svg?react";
import MenuContext from "contexts/MenuContext";
import AttachmentIconComponent from "components/AttachmentIconComponent";
import { IStatus } from "dto/IStatus";
import HistoryModal from "components/HistoryModal";
import ChooseTagNew from "components/ChooseTagNew";
import StatusModal from "components/StatusModal";
import StatusModalLarge from "components/StatusModalLarge";
import DossierMoveModal from "components/DossierMoveModal";
import DossierCreateVersionModal from "components/DossierCreateVersionModal";
import {
  getDossierItemRealFileName,
} from "utils/getDossierItemFileName";
import PackageEditSharesModal from "components/PackageEditSharesModal";
import sanitizeHtml from "sanitize-html";
import InputInline from "components/InputInline";
import PluginNotExist from "components/PluginNotExist";
import { useTranslation } from "react-i18next";

const DossierFile: FC<Props> = ({
  selectedFiles,
  canSelect,
  onSelect,
  file,
  statuses,
  tags,
  index,
  refetch: refetchAll,
  cutting,
  onClick,
  isSelected: selectedC,
  inFolder = false,
  handleCopy,
  handleFileDoubleClick,
  collapsed = false,
}) => {
  const { t } = useTranslation();
  const { caseId } = useParams();
  const [showHistory, setShowHistory] = useState(false);
  const [, setShowMenu] = useState(false);
  const [downloadProgress] = useState(0);
  const [label, setLabel] = useState<string>(file.Label);
  const [showPluginError, setShowPluginError] = useState<boolean>(false);
  // const { data: caseData } = casesService.useGetCase(caseId!);

  const { id } = useContext(MenuContext);

  useEffect(() => {
    if (id !== file.Id) {
      setShowMenu(false);
    }
  }, [id]);

  const handleClick = () => {
    onClick?.();
    onSelect?.(file);
  };

  const handleDoubleClick = () => {
    if (canSelect) {
      onSelect?.(file);
      return;
    }

    handleFileDoubleClick?.(file);
  };

  const { menu, shares } = useContext(DossierFilterContext);

  const [status, setStatus] = useState<IStatus | null>(null);
  const [showTags, setShowTags] = useState(false);
  const [showTagsSmall, setShowTagsSmall] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [showStatusLarge, setShowStatusLarge] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [showCreateVersion, setShowCreateVersion] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const refWrapper = useRef(null);
  const refTag = useRef(null);
  const refStatus = useRef(null);
  const [, setFindedTags] = useState<ITag[]>([]);

  const { mutate: deleteDossier } = dossierService.useDeleteDossier();

  useEffect(() => {
    const t = tags?.filter((tag) =>
      file?.TagIds?.find((iTag) => tag.Id === iTag),
    );
    const newStatus = statuses.find((s) => s.Id === file.StatusId);

    setFindedTags(t);
    setStatus(newStatus ?? null);
  }, [statuses, file?.TagIds]);

  const onDeleteClick = () => {
    deleteDossier(
      {
        Id: file.DossierId,
      },
      {
        onSuccess: () => refetchAll(),
      },
    );
  };

  const { dossierFiles, setDossierFiles } = useContext(DossierFilesContext);

  const handleUpdateTags = (tags: ITag[]) => {
    const files = [...dossierFiles];

    const el = files.find((c) => c.Id === file.Id && c.Kind === file.Kind);

    if (el) {
      el.TagIds = tags.map((t) => t.Id);
    }

    setDossierFiles(files);
  };

  const handleStatusUpdate = (status?: IStatus) => {
    setStatus(status);
    setShowStatus(false);
  };

  const isSelected = !!selectedFiles?.find(
    (s) => s?.DossierId === file?.DossierId,
  );

  // useEffect(() => {
  //   if (isSelected && refWrapper.current) {
  //     refWrapper.current!.scrollIntoView();
  //   }
  // }, [isSelected]);

  const [isSameCase, setIsSameCase] = useState(false);
  const [isFindedElement, setIsFindedElement] = useState(false);

  useEffect(() => {
    const copyElements = JSON.parse(
      sessionStorage.getItem("copy_elements") ?? "[]",
    );
    const copyCase = JSON.parse(sessionStorage.getItem("copy_case") ?? "[]");

    setIsSameCase(copyCase?.Id === caseId);
    setIsFindedElement(!!copyElements.find((copy: any) => copy.Id === file.Id));
  }, [caseId, selectedFiles, cutting]);

  const [editLabel, setEditLabel] = useState(false);

  useEffect(() => {
    if (editLabel) {
      setLabel(file?.Label);
    }
  }, [editLabel]);

  const [mouseIs, setMouseIs] = useState(false);

  const [copy, setCopy] = useState(false);
  const [cut, setCut] = useState(false);

  const handleButton = (e: any) => {
    if (e.key === "F2") {
      setEditLabel(true);
    } else if (e.key === "Delete") {
      handleDelete(file);
    }
  };

  const { mutate: updateLabel } = dossierService.useUpdateLabel();

  const commitLabel = (newLabel) => {
    updateLabel(
      {
        Id: file.DossierId,
        Label: newLabel ?? "",
      },
      {
        onSuccess: () => {
          refetchAll();
          setEditLabel(false);
        },
      },
    );
  };

  const cancelLabelEdit = () => {
    setLabel(file.Label);
    setEditLabel(false);
  };

  const handleDelete = () => {
    const ask = localStorage.getItem("ask");

    if (ask === "true") {
      onDeleteClick();
      return;
    }

    setShowDelete(true);
  };

  useEffect(() => {
    if (!mouseIs) return;
    window.addEventListener("keydown", handleButton);

    return () => {
      window.removeEventListener("keydown", handleButton);
    };
  }, [mouseIs]);

  useEffect(() => {
    if (!selectedFiles?.length) return;
    if (copy) {
      handleCopy(false);
      setCopy(false);
      return;
    }
    if (cut) {
      handleCopy(true);
      setCut(false);
      return;
    }
  }, [selectedFiles, copy, cut]);


  const rippleContainerRef = useRef(null);

  const createRipple = (event: MouseEvent) => {
    const container = rippleContainerRef.current as HTMLElement;
    if (!container) return;

    // Usuń poprzedni ripple, jeśli istnieje
    const oldRipple = container.querySelector(`.${styles.ripple}`);
    if (oldRipple) {
      oldRipple.remove();
    }

    // Utwórz nowy element ripple
    const ripple = document.createElement("span");
    const diameter = Math.max(container.clientWidth, container.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${event.clientX - container.offsetLeft - radius}px`;
    ripple.style.top = `${event.clientY - container.offsetTop - radius}px`;
    ripple.classList.add(styles.ripple);
    container.style.overflow = "hidden";

    // Dodaj ripple do kontenera
    container.appendChild(ripple);

    // Usuń ripple po zakończeniu animacji (600ms)
    setTimeout(() => {
      ripple.remove();
      container.style.overflow = "visible";
    }, 600);
  };

  return (
    <>
      <Draggable
        index={index}
        key={file.DossierId}
        draggableId={file?.DossierId}
      >
        {(provided) => {
          //@ts-ignore
          provided.draggableProps.style = {
            ...provided.draggableProps.style,
            //@ts-ignore
            cursor: "pointer",
          };
          return (
            <div
              data-id={file.DossierId}
              ref={rippleContainerRef}
              onClick={(e) => {
                createRipple(e);
              }}
              className={`${styles.rippleContainer}`}
            >
              <div
                onMouseEnter={() => setMouseIs(true)}
                onMouseLeave={() => setMouseIs(false)}
                onContextMenu={(e: any) => {
                  e.preventDefault();
                  setShowMenu(true);
                }}
                className={`${styles.wrapper} ${selectedC && styles.active} ${
                  isSelected && !copy && styles.active
                }`}
                ref={refWrapper}
              >
                {/* {mouseIs && (
                <div className={styles.grip}>
                  <FontAwesomeIcon icon={faGripVertical} />
                </div>
              )} */}

                {showTags && (
                  <ModalLayout
                    dock={DockPosition.DISABLED}
                    off={true}
                    customX={1}
                    isAnimation={true}
                    customY={0}
                    ignore
                    fullHeight={true}
                    toggle={() => setShowTags(!showTags)}
                  >
                    <AddTagModal
                      toggle={setShowTags}
                      updateTags={handleUpdateTags}
                      //@ts-ignore
                      toTag={file as IPackage}
                    />
                  </ModalLayout>
                )}

                {showTagsSmall && (
                  <ModalLayout
                    dock={DockPosition.BottomRight}
                    refWrapper={refTag}
                    onEnd={() => {
                      setShowTags(false);
                    }}
                    disableMove
                    toggle={() => setShowTagsSmall(!showTagsSmall)}
                  >
                    {/* <AddTagModal
                  toggle={() => {
                    setShowTags(false);
                    toggle?.();
                  }}
                  updateTags={handleUpdateTags}
                  toTag={pack}
                /> */}
                    <div className={styles.tagModal}>
                      <ChooseTagNew
                        toggle={setShowTagsSmall}
                        updateTags={handleUpdateTags}
                        //@ts-ignore
                        toTag={file as IPackage}
                      />
                    </div>
                  </ModalLayout>
                )}

                {showStatus && (
                  <ModalLayout
                    dock={DockPosition.DISABLED}
                    customX={0}
                    customY={-32}
                    refWrapper={refStatus}
                    toggle={() => setShowStatus(!showStatus)}
                  >
                    <StatusModal
                      item={file}
                      toggle={setShowStatus}
                      selectedStatus={status}
                      afterUpdate={handleStatusUpdate}
                    />
                  </ModalLayout>
                )}

                {showStatusLarge && (
                  <ModalLayout
                    dock={DockPosition.DISABLED}
                    off={true}
                    customX={1}
                    isAnimation={true}
                    customY={0}
                    ignore
                    fullHeight={true}
                  >
                    <StatusModalLarge
                      toggle={setShowStatusLarge}
                      item={file}
                      selectedStatus={status}
                    />
                  </ModalLayout>
                )}

                {showMove && (
                  <ModalLayout
                    dock={DockPosition.DISABLED}
                    off={true}
                    customX={1}
                    isAnimation={true}
                    customY={0}
                    ignore
                    fullHeight={true}
                  >
                    <DossierMoveModal
                      toggle={setShowMove}
                      sourceCaseId={caseId ?? ""}
                      item={file}
                      refetch={refetchAll}
                    />
                  </ModalLayout>
                )}

                {showCreateVersion && (
                  <ModalLayout
                    dock={DockPosition.DISABLED}
                    off={true}
                    customX={1}
                    isAnimation={true}
                    customY={0}
                    ignore
                    fullHeight={true}
                  >
                    <DossierCreateVersionModal
                      toggle={setShowCreateVersion}
                      item={file}
                      refetch={refetchAll}
                    />
                  </ModalLayout>
                )}

                {showShare && (
                  <ModalLayout
                    dock={DockPosition.DISABLED}
                    off={true}
                    customX={1}
                    isAnimation={true}
                    customY={0}
                    ignore
                    fullHeight={true}
                  >
                    <PackageEditSharesModal
                      toggle={() => setShowShare(false)}
                      item={file}
                      refetch={refetchAll}
                    />
                  </ModalLayout>
                )}

                <div
                  onClick={handleClick}
                  onDoubleClick={handleDoubleClick}
                  className={`${styles.file} ${inFolder && styles.folder} ${
                    isSameCase && isFindedElement && cutting && styles.cut
                  }`}
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  title={file.FileName}
                >
                  <div
                    className={`${styles.fileName} ${
                      file?.Type === DossierItemType.Link && styles.link
                    }`}
                  >
                    {file?.Type !== DossierItemType.Link && (
                      <AttachmentIconComponent
                        className={styles.iconPDF}
                        file={getDossierItemRealFileName(file)}
                      />
                    )}
                    {file?.Type === DossierItemType.Link && (
                      <LinkIcon className={styles.iconPDF} />
                    )}
                    {!collapsed && !editLabel && (
                      <div
                        className={styles.name}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(
                            file.Label?.length
                              ? file.Label
                              : file?.FileName ?? "",
                          ),
                        }}
                      ></div>
                    )}
                    {!collapsed && editLabel && (
                      <InputInline
                        value={label}
                        placeholder={file.FileName}
                        onChange={commitLabel}
                        onCancel={cancelLabelEdit}
                      ></InputInline>
                    )}
                  </div>
                  {!collapsed && !editLabel && (
                    <div className={styles.information}>
                      {menu
                        .filter((x) => !x.hidden && (!x.shares || shares))
                        .map((m) => (
                          <div
                            className={`${styles.box} ${
                              m.type === "sharedBy" || m.type === "sharedTo"
                                ? styles.large
                                : ""
                            } ${m.type === "size" ? styles.small : ""}`}
                          >
                            {<></>}
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                <div className={styles.progressBar}>
                  {file?.progress && (
                    <div
                      style={{ width: `${file?.progress ?? 0}%` }}
                      className={styles.progress}
                    ></div>
                  )}
                  <div
                    style={{
                      width: `${downloadProgress}%`,
                    }}
                    className={styles.progress}
                  ></div>
                </div>
              </div>
            </div>
          );
        }}
      </Draggable>
      {/* {editLabel && (
        <ModalLayout
          dock={DockPosition.DISABLED}
          off={true}
          customX={1}
          isAnimation={true}
          customY={0}
          ignore
          fullHeight={true}
          refWrapper={refWrapper}
          toggle={() => setEditLabel(!editLabel)}
        >
          <EditDossier toggle={setEditLabel} refetch={refetchAll} file={file} />
        </ModalLayout>
      )} */}

      {showHistory && (
        <ModalLayout
          // toggle={() => setShowVersion(false)}
          dock={DockPosition.BottomRight}
          customX={0}
          refWrapper={refWrapper}
        >
          <HistoryModal
            pack={file as any}
            toggle={() => setShowHistory(false)}
          />
          {/* <></> */}
        </ModalLayout>
      )}

      {showDelete && (
        <ConfirmModal
          title={t("confirm.deleteQuestion")}
          text={t("confirm.deleteSelectedItem")}
          acceptLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onAccept={() => {
            setShowDelete(false);
            onDeleteClick();
          }}
          onCancel={() => setShowDelete(false)}
        />
      )}

      {showPluginError && (
        <PluginNotExist handleClose={() => setShowPluginError(false)} />
      )}
    </>
  );
};

export default DossierFile;
